import React, { useState, useEffect } from 'react';
import { 
  Menu, 
  X, 
  ChevronRight, 
  Phone, 
  Mail, 
  MapPin, 
  Camera, 
  Music, 
  Utensils, 
  Users, 
  Truck, 
  Sparkles, 
  Building2, 
  Award,
  ArrowRight,
  MessageCircle,
  Facebook,
  Instagram,
  Linkedin,
  Star,
  Heart,
  Palette
} from 'lucide-react';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });

  // Logo path from the provided file
  const logoPath = "sm-logo.png";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { title: 'The Royal Mandap', icon: <Heart className="w-8 h-8" />, desc: 'Bespoke decor for Muhurthams and ceremonies that blend tradition with modern luxury.' },
    { title: 'Sangeet & Entertainment', icon: <Music className="w-8 h-8" />, desc: 'High-energy choreography, celebrity performers, and concert-grade AV production.' },
    { title: 'Gourmet Indian Feast', icon: <Utensils className="w-8 h-8" />, desc: 'Authentic multi-regional cuisines served with five-star hospitality and styling.' },
    { title: 'Wedding Concierge', icon: <Users className="w-8 h-8" />, desc: 'End-to-end guest management, hospitality desks, and RSVP coordination.' },
    { title: 'Cinematic Memories', icon: <Camera className="w-8 h-8" />, desc: 'Premium wedding films and photography capturing every emotional ritual.' },
    { title: 'Corporate Galas', icon: <Building2 className="w-8 h-8" />, desc: 'Sophisticated conferences and award nights for Bengaluru’s top enterprises.' },
    { title: 'Theme & Concept', icon: <Palette className="w-8 h-8" />, desc: 'Innovative mood boards and set design that transform venues into dreams.' },
    { title: 'Logistics Control', icon: <Truck className="w-8 h-8" />, desc: 'Flawless execution of move-ins, vendor sync, and on-ground management.' },
  ];

  const highlights = [
    { label: 'Years of Magic', value: '10+' },
    { label: 'Royal Weddings', value: '300+' },
    { label: 'Corporate Partners', value: '150+' },
    { label: 'Seamless Execution', value: '100%' },
  ];

  const socialLinks = {
    facebook: "https://www.facebook.com/smevents.sm?mibextid=LQQJ4d",
    instagram: "https://instagram.com/_smeventsandproductions?igshid=NTc4MTIwNjQ2YQ==",
    linkedin: "#"
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWhatsAppInquiry  = (e) => {
      e.preventDefault();
      const phoneNumber = "919008342225";
      const message = `*Inquiry from SM Events Website*\n\nHello! I would like to inquire about availability.\n\n*Host Name:* ${formData.name || 'Not provided'}\n*Email:* ${formData.email || 'Not provided'}\n\nPlease let me know the next steps for a consultation.`;
      window.open(`https://wa.me/919008342225?text=${encodeURIComponent(message)}`, '_blank');
  };

  const goldGradient = "bg-gradient-to-tr from-[#8E6E2F] via-[#D4AF37] to-[#F9E4B7]";
  const goldText = "text-transparent bg-clip-text bg-gradient-to-b from-[#F9E4B7] via-[#D4AF37] to-[#8E6E2F]";

  return (
    <div className="min-h-screen bg-[#070707] text-slate-100 font-serif selection:bg-amber-600/40">
      {/* Floating WhatsApp Button */}
      <a 
        href="https://wa.me/919008342225" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[100] bg-[#25D366] text-white p-4 rounded-full shadow-[0_0_30px_rgba(37,211,102,0.3)] hover:scale-110 transition-all duration-300 flex items-center justify-center group"
      >
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:mr-2 transition-all duration-500 whitespace-nowrap font-sans font-bold text-sm">
          Discuss Your Event
        </span>
        <MessageCircle size={28} fill="currentColor" />
      </a>

      {/* Fixed Navigation */}
      <nav className={`fixed w-full z-[1000] transition-all duration-500 ${scrolled ? 'bg-[#070707]/95 backdrop-blur-xl py-3 border-b border-amber-900/40' : 'bg-transparent py-8'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src={logoPath} alt="SM Events" className="h-10 md:h-14 w-auto drop-shadow-[0_0_10px_rgba(212,175,55,0.4)]" />
            <div className="flex flex-col leading-none border-l border-amber-800/40 pl-4 hidden sm:flex">
              <span className={`font-bold text-xl md:text-2xl tracking-[0.2em] font-sans ${goldText}`}>SM EVENTS</span>
              <span className="text-[8px] md:text-[9px] uppercase tracking-[0.45em] font-sans font-medium text-amber-200/50 mt-1">Delivering Impactful Experiences</span>
            </div>
          </div>

          <div className="hidden md:flex gap-10 font-sans font-medium text-[10px] uppercase tracking-[0.3em] text-amber-100/70">
            <a href="#home" className="hover:text-amber-400 transition-colors">Home</a>
            <a href="#services" className="hover:text-amber-400 transition-colors">Services</a>
            <a href="#about" className="hover:text-amber-400 transition-colors">Legacy</a>
            <a href="#portfolio" className="hover:text-amber-400 transition-colors">Gallery</a>
            <a href="#contact" className="bg-amber-500/10 border border-amber-500/30 px-6 py-2 rounded-full hover:bg-amber-500 hover:text-black transition-all">Plan Event</a>
          </div>

          <button className="md:hidden text-amber-400" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`fixed inset-0 bg-[#070707] z-[-1] flex flex-col items-center justify-center gap-8 transition-all duration-500 md:hidden ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <a href="#home" onClick={() => setIsMenuOpen(false)} className="text-3xl font-sans tracking-widest text-amber-100">Home</a>
          <a href="#services" onClick={() => setIsMenuOpen(false)} className="text-3xl font-sans tracking-widest text-amber-100">Services</a>
          <a href="#about" onClick={() => setIsMenuOpen(false)} className="text-3xl font-sans tracking-widest text-amber-100">Our Story</a>
          <a href="#contact" onClick={() => setIsMenuOpen(false)} className={`text-2xl font-sans font-bold tracking-widest ${goldText}`}>Contact Us</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0">
           <img 
            src="https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&q=80" 
            className="w-full h-full object-cover scale-100 animate-slow-zoom opacity-50" 
            alt="Royal Indian Wedding" 
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-[#070707]"></div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mt-20">
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-6">
              <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-amber-500"></div>
              <Sparkles className="text-amber-400" size={24} />
              <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
          </div>
          <span className="text-amber-200 font-sans font-light tracking-[0.7em] uppercase mb-6 block text-xs md:text-sm">
            Curators of Grand Celebrations
          </span>
          <h1 className="text-5xl md:text-8xl font-bold mb-10 leading-[1.1] tracking-tight">
            Crafting The <br/>
            <span className={goldText}>Big Fat Wedding</span>
          </h1>
          <p className="text-slate-300 font-sans text-base md:text-xl mb-12 max-w-3xl mx-auto font-light leading-relaxed tracking-wide">
            Bengaluru's premier atelier for luxury Indian weddings and high-profile corporate galas. 
            Where tradition meets technical brilliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center font-sans">
            <button className={`${goldGradient} text-black px-14 py-5 font-bold uppercase tracking-[0.3em] text-[10px] transition-all shadow-[0_15px_40px_rgba(212,175,55,0.2)] hover:shadow-[0_20px_50px_rgba(212,175,55,0.4)] hover:-translate-y-1`}>
              Start Planning
            </button>
            <button className="border border-amber-500/50 hover:bg-amber-500/10 text-amber-200 px-14 py-5 font-bold uppercase tracking-[0.3em] text-[10px] transition-all backdrop-blur-md">
              View Portfolio
            </button>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0c0c0c] border-y border-amber-900/30 py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-12">
          {highlights.map((stat, idx) => (
            <div key={idx} className="text-center group">
              <div className={`text-5xl font-bold mb-3 tracking-tighter ${goldText}`}>{stat.value}</div>
              <div className="text-[10px] text-amber-100/40 uppercase tracking-[0.4em] font-sans font-black group-hover:text-amber-300 transition-colors">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-32 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-28">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 italic tracking-tight text-white">Grandeur In Every Detail</h2>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[1px] w-20 bg-amber-900/50"></div>
            <Star size={18} className="text-amber-500 fill-amber-500" />
            <div className="h-[1px] w-20 bg-amber-900/50"></div>
          </div>
          <p className="text-slate-400 font-sans max-w-2xl mx-auto leading-loose text-sm md:text-base tracking-wide italic">
            "Delivering Impactful Experiences from the sacred Muhurtham to the high-energy Reception."
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-amber-900/20 border border-amber-900/20 shadow-2xl">
          {services.map((service, idx) => (
            <div key={idx} className="group p-12 bg-[#080808] hover:bg-[#0c0c0c] transition-all duration-700 relative overflow-hidden border border-white/5">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-100 transition-opacity">
                <ChevronRight size={16} className="text-amber-500" />
              </div>
              <div className="text-amber-500/70 mb-10 transform group-hover:scale-110 group-hover:text-amber-400 transition-all duration-700">
                {service.icon}
              </div>
              <h3 className="text-lg font-bold mb-5 text-white uppercase tracking-[0.15em] leading-tight">{service.title}</h3>
              <p className="text-slate-400 font-sans text-[11px] leading-relaxed mb-8 group-hover:text-slate-200 transition-colors tracking-wide">{service.desc}</p>
              <div className="w-8 h-[2px] bg-amber-700 group-hover:w-full transition-all duration-700"></div>
            </div>
          ))}
        </div>
      </section>

      {/* About/Showcase */}
      <section id="about" className="py-32 bg-[#050505] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-amber-500/5 blur-[120px] rounded-full translate-x-1/2"></div>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-24 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="relative z-10 aspect-[4/5] overflow-hidden border border-amber-500/20">
              <img 
                src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?auto=format&fit=crop&q=80" 
                className="w-full h-full object-cover transition-all duration-1000 hover:scale-105"
                alt="Mandap Decor" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
            </div>
            <div className="absolute -top-10 -left-10 w-40 h-40 border-l-2 border-t-2 border-amber-500/30"></div>
            <div className="absolute -bottom-10 -right-10 bg-[#080808] border border-amber-900/30 p-12 z-20 shadow-2xl max-w-xs">
              <Award size={44} className="text-amber-500 mb-6" />
              <h4 className="text-2xl font-bold mb-2">The Golden Standard</h4>
              <p className="text-amber-400/60 font-sans uppercase tracking-[0.2em] text-[10px]">Since 2014</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-amber-500 font-sans font-bold uppercase tracking-[0.5em] text-[10px] mb-8 block">Our Wedding Legacy</span>
            <h2 className={`text-5xl md:text-6xl font-bold mb-12 italic leading-[1.2] ${goldText}`}>Tradition Meets <br/>Modern Splendor</h2>
            <p className="text-slate-300 font-sans text-lg mb-10 leading-relaxed font-light text-balance border-l border-amber-900/50 pl-8">
              "We understand the gravity of Indian rituals. From the first Haldi splash to the final Vidaai, we curate a tapestry of moments that stay immortal."
            </p>
            <div className="space-y-8 mb-16 font-sans">
              {[
                { label: 'Royal Mandap Design', sub: 'Thematic decor with imported florals' },
                { label: 'Technical AV Excellence', sub: 'Concert-grade sound for Sangeet nights' },
                { label: 'Hospitality Management', sub: 'White-glove service for all your guests' }
              ].map((item, i) => (
                <div key={i} className="flex gap-6 group">
                  <div className="w-12 h-12 rounded-full border border-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500 transition-all duration-500">
                    <Star size={16} className="text-amber-500 group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <h5 className="text-white font-bold uppercase tracking-widest text-xs mb-1">{item.label}</h5>
                    <p className="text-slate-500 text-[11px] tracking-wide">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Gallery */}
      <section id="portfolio" className="py-32 px-6 bg-[#070707]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-10">
            <div>
              <h2 className="text-6xl font-bold text-white mb-6 italic tracking-tight">The Collection</h2>
              <p className="text-amber-200/40 font-sans tracking-[0.3em] uppercase text-[10px]">Masterpieces in event production</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-7 group relative overflow-hidden aspect-[16/10] border border-white/5 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&q=80" className="w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-1000" alt="Wedding Grandeur" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"></div>
              <div className="absolute bottom-12 left-12">
                <span className="text-amber-500 font-sans text-[10px] uppercase tracking-[0.5em] mb-4 block">Celebration</span>
                <h4 className="text-3xl font-bold italic">Palatial Heritage Reception</h4>
              </div>
            </div>
            <div className="md:col-span-5 group relative overflow-hidden aspect-[4/5] border border-white/5 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1583939411023-14783179e581?auto=format&fit=crop&q=80" className="w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-1000" alt="Haldi Ceremony" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"></div>
              <div className="absolute bottom-12 left-12">
                <span className="text-amber-500 font-sans text-[10px] uppercase tracking-[0.5em] mb-4 block">Ceremony</span>
                <h4 className="text-3xl font-bold italic">Vibrant Floral Haldi</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-32 px-6 max-w-7xl mx-auto">
        <div className="text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 italic tracking-tight text-white">About Us</h2>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[1px] w-20 bg-amber-900/50"></div>
            <Star size={18} className="text-amber-500 fill-amber-500" />
            <div className="h-[1px] w-20 bg-amber-900/50"></div>
          </div>
        </div>

        <div className="grid gap-px bg-amber-900/20 border border-amber-900/20 shadow-2xl">
          Welcome to SANDEEP, SM Events & Productions — your trusted partner for creating unforgettable experiences. Based in Bengaluru, India, we specialize in delivering tailor-made event planning and production services that bring your vision to life with creativity, precision, and flair.<br/><br/>
          At SM Events, we understand that every celebration is unique. Whether it’s a wedding, corporate gala, birthday, anniversary, or social gathering, our dedicated team of professionals works closely with you to design and execute events that are memorable and seamless from start to finish.<br/><br/>
          Our expertise spans across a wide range of event services, including:<br/><br/>
          🎉 Full-Service Event Planning — Concept development, timeline coordination, and execution<br/>
          📸 Photography & Videography — Capturing moments with cinematic quality<br/>
          💐 Décor & Styling — Customized décor for every theme and ambience<br/>
          👰 Bridal Services — Makeup, hair styling, and mehndi designs tailored to your look<br/>
          🍽️ Catering & Entertainment — Exceptional food and entertainment for guests of all ages<br/>
          🚗 Logistics & Venue Management — Smooth coordination behind the scenes<br/><br/>
          We combine creative vision, meticulous planning, and professional execution to ensure your event not only meets but exceeds expectations. From the smallest detail to the grandest moment, SM Events is committed to making your special occasion truly extraordinary.<br/><br/>
          Let’s create memorable moments together.<br/>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-[#080808] border border-amber-900/30 flex flex-col lg:flex-row overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.6)]">
            <div className="p-16 lg:w-1/3 bg-[#080808] border-r border-amber-900/20">
              <img src={logoPath} alt="SM Events" className="h-20 w-auto mb-12 opacity-80" />
              <h2 className={`text-4xl font-bold mb-10 italic leading-tight ${goldText}`}>Plan Your Grand Celebration</h2>
              <div className="space-y-12 font-sans">
                <div className="flex gap-6">
                  <MapPin className="text-amber-500 w-6 h-6 flex-shrink-0" />
                  <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 leading-loose">
                    <span className="text-white block mb-2 font-bold">SANDEEP</span>
                    4th Floor, No 26/13, Flat No-403, Arya Vishwash Apartment, 2nd Block, 10th Main Road,, Banashankari Ist Stage, <br/>Bengaluru, Bengaluru (Bangalore) Urban, Karnataka, 560050
                  </div>
                </div>
                <div className="flex gap-6">
                  <Phone className="text-amber-500 w-6 h-6" />
                  <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400">
                    <span className="text-white block mb-2 font-bold">Concierge Desk</span>
                    <a href="mailto:sandeep@smevents.co.in" className="hover:text-amber-400 transition-colors font-bold">sandeep@smevents.co.in</a><br/>
                    <a href="tel:+919008342225" className="hover:text-amber-400 transition-colors font-bold">+91 90083 42225</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-16 lg:w-2/3 bg-[#0c0c0c]">
              <form className="grid grid-cols-1 md:grid-cols-2 gap-12 font-sans" onSubmit={handleWhatsAppInquiry}>
                <div className="group">
                  <label className="text-[10px] font-black uppercase text-amber-500 mb-4 block tracking-[0.4em]">Host Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full bg-transparent border-b border-amber-900/30 focus:border-amber-400 py-3 outline-none transition-all placeholder:text-slate-800 text-amber-100" placeholder="MR. & MRS. SHARMA" />
                </div>
                <div className="group">
                  <label className="text-[10px] font-black uppercase text-amber-500 mb-4 block tracking-[0.4em]">Email Contact</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full bg-transparent border-b border-amber-900/30 focus:border-amber-400 py-3 outline-none transition-all placeholder:text-slate-800 text-amber-100" placeholder="CONTACT@CELEBRATION.COM" />
                </div>
                <div className="md:col-span-2">
                  <button type="submit" className={`${goldGradient} text-black w-full py-6 font-black uppercase tracking-[0.5em] text-[10px] shadow-2xl hover:shadow-amber-500/20 transition-all active:scale-95`}>
                    Inquire Availability
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black pt-32 pb-16 border-t border-amber-900/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-20 mb-20">
            <div className="max-w-sm">
              <img src={logoPath} alt="SM Events" className="h-14 w-auto mb-10 grayscale brightness-125 opacity-60" />
              <p className="text-slate-500 font-sans text-[11px] leading-[2] tracking-widest uppercase">
                Redefining the landscape of luxury event production in Bengaluru. Specializing in high-impact Indian weddings and corporate events.
              </p>
            </div>
            <div className="flex gap-8">
              <a href={socialLinks.instagram} target="_blank" className="text-amber-500/50 hover:text-amber-400 transition-all"><Instagram size={24} /></a>
              <a href={socialLinks.facebook} target="_blank" className="text-amber-500/50 hover:text-amber-400 transition-all"><Facebook size={24} /></a>
            </div>
          </div>
          <div className="pt-12 border-t border-white/5 text-[9px] uppercase tracking-[0.4em] text-slate-600">
            © 2024, SANDEEP, SM Events & Productions. Bengaluru's Luxury Landmark.
          </div>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes slow-zoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }
        .animate-slow-zoom {
          animation: slow-zoom 20s infinite alternate ease-in-out;
        }
      `}} />
    </div>
  );
};

export default App;